import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Landmark, GraduationCap, MessageCircle, Sparkles, Lock, Plus, Trash2, Mail, CheckCircle2, ThumbsUp, Wand2, ScrollText } from "lucide-react";

const STATUS_COLORS = {
  new: "bg-primary/20 text-gold",
  reviewing: "bg-[hsl(32_87%_62%/0.2)] text-[hsl(32_60%_65%)]",
  planned: "bg-gold text-secondary-foreground",
  shipped: "bg-[hsl(150_60%_45%/0.2)] text-[hsl(150_60%_65%)]",
  rejected: "bg-muted text-muted-foreground",
};

const SEAT_STATUS = {
  active: "bg-primary/20 text-gold",
  expiring_soon: "bg-[hsl(32_87%_62%/0.2)] text-[hsl(32_60%_65%)]",
  expired: "bg-destructive/20 text-[hsl(355_80%_75%)]",
};

function LockedGate({ addon }) {
  return (
    <div className="hoa-card royal-ring p-10 text-center" data-testid={`${addon.toLowerCase().replace(/\s+/g,'-')}-locked`}>
      <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
      <h2 className="text-2xl font-semibold mb-2">{addon} is a premium add-on</h2>
      <p className="no-gold text-foreground/75 max-w-xl mx-auto mb-6">Start your 14-day free trial from Subscription → Add-ons.</p>
      <a href="/subscription" className="inline-flex items-center gap-2 bg-gold text-secondary-foreground px-5 py-3 rounded-full font-semibold hover:brightness-110 glow-gold">
        <Sparkles className="w-4 h-4" /> Start free trial
      </a>
    </div>
  );
}

export default function BoardOps() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const isAdmin = user?.role === "admin";

  // Elections
  const [seats, setSeats] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [owners, setOwners] = useState([]);
  const [electionsLocked, setElectionsLocked] = useState(false);
  const [seatOpen, setSeatOpen] = useState(false);
  const [seatForm, setSeatForm] = useState({ title: "President", holder_id: "", term_end: "" });
  const [candOpen, setCandOpen] = useState(false);
  const [candForm, setCandForm] = useState({ seat_id: "", years_in_community: 1, background: "", priorities: "" });
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  // Training
  const [modules, setModules] = useState([]);
  const [trainingLocked, setTrainingLocked] = useState(false);

  // Feedback (always available)
  const [feedback, setFeedback] = useState([]);
  const [fbOpen, setFbOpen] = useState(false);
  const [fbForm, setFbForm] = useState({ title: "", body: "", category: "idea" });

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/elections/seats"), api.get("/elections/candidates")]);
      setSeats(s.data); setCandidates(c.data); setElectionsLocked(false);
    } catch (err) { if (err.response?.status === 402) setElectionsLocked(true); }
    try {
      const { data } = await api.get("/training/modules");
      setModules(data); setTrainingLocked(false);
    } catch (err) { if (err.response?.status === 402) setTrainingLocked(true); }
    try {
      const { data } = await api.get("/feedback");
      setFeedback(data);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("swallowed:", err?.message || err); }
    try {
      const { data } = await api.get("/users");
      setOwners(data);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("swallowed:", err?.message || err); }
  };

  useEffect(() => { load(); }, []);

  const createSeat = async (e) => {
    e.preventDefault();
    try {
      await api.post("/elections/seats", { ...seatForm, holder_id: seatForm.holder_id || null });
      toast.success("Seat added");
      setSeatOpen(false);
      setSeatForm({ title: "President", holder_id: "", term_end: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add"); }
  };

  const delSeat = async (id) => {
    if (!confirm("Delete this seat?")) return;
    try { await api.delete(`/elections/seats/${id}`); load(); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("swallowed:", err?.message || err); }
  };

  const genEmail = async (sid) => {
    setEmailLoading(true); setEmailOpen(true); setEmailDraft(null);
    try {
      const { data } = await api.post(`/elections/notice/${sid}`);
      setEmailDraft(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not generate"); setEmailOpen(false); }
    finally { setEmailLoading(false); }
  };

  const registerCandidate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/elections/candidates", { ...candForm, years_in_community: Number(candForm.years_in_community) });
      toast.success("Candidacy registered — AI biography drafted");
      setCandOpen(false);
      setCandForm({ seat_id: "", years_in_community: 1, background: "", priorities: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not register"); }
  };

  const completeModule = async (mid) => {
    try {
      await api.post("/training/complete", { module_id: mid });
      toast.success("Marked complete");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not complete"); }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    try {
      await api.post("/feedback", fbForm);
      toast.success("Feedback submitted — kept for developer review");
      setFbOpen(false);
      setFbForm({ title: "", body: "", category: "idea" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not submit"); }
  };

  const upvoteFb = async (id) => {
    try { await api.post(`/feedback/${id}/upvote`); load(); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("swallowed:", err?.message || err); }
  };

  const setFbStatus = async (id, status) => {
    try {
      const note = prompt("Developer note (optional):") || "";
      await api.patch(`/feedback/${id}/status`, { status, developer_note: note });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="space-y-6" data-testid="board-ops-page">
      <div>
        <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
          <Landmark className="w-4 h-4" /> Board Operations
        </div>
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">Elections. Training. Your voice.</h1>
        <p className="no-gold text-foreground/75 mt-2 max-w-2xl">
          Track board terms with automated notices. Train new directors in minutes. Send us ideas — the feedback
          forum reaches our developers.
        </p>
      </div>

      <Tabs defaultValue="elections">
        <TabsList className="bg-card border border-border" data-testid="board-ops-tabs">
          <TabsTrigger value="elections" data-testid="tab-elections">Elections</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training">Training Kits</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
        </TabsList>

        {/* Elections */}
        <TabsContent value="elections" className="mt-6 space-y-6">
          {electionsLocked ? <LockedGate addon="Board Elections Kit" /> : (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Board seats</h3>
                <div className="flex gap-2">
                  {isBoard && (
                    <Dialog open={seatOpen} onOpenChange={setSeatOpen}>
                      <DialogTrigger asChild>
                        <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110" data-testid="new-seat-btn">
                          <Plus className="w-4 h-4 mr-1.5" /> Add seat
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Add a board seat</DialogTitle></DialogHeader>
                        <form onSubmit={createSeat} className="space-y-4" data-testid="seat-form">
                          <div>
                            <Label>Title</Label>
                            <Input required value={seatForm.title} onChange={(e) => setSeatForm({ ...seatForm, title: e.target.value })} className="mt-1.5 h-11" placeholder="President / Treasurer / Secretary" data-testid="seat-title" />
                          </div>
                          <div>
                            <Label>Current holder</Label>
                            <Select value={seatForm.holder_id} onValueChange={(v) => setSeatForm({ ...seatForm, holder_id: v })}>
                              <SelectTrigger className="mt-1.5 h-11" data-testid="seat-holder"><SelectValue placeholder="(vacant)" /></SelectTrigger>
                              <SelectContent>
                                {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Term end</Label>
                            <Input required type="date" value={seatForm.term_end} onChange={(e) => setSeatForm({ ...seatForm, term_end: e.target.value })} className="mt-1.5 h-11" data-testid="seat-term-end" />
                          </div>
                          <DialogFooter>
                            <Button type="submit" className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="seat-submit">Add</Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                  <Dialog open={candOpen} onOpenChange={setCandOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="rounded-full h-11" data-testid="run-candidate-btn"><Sparkles className="w-4 h-4 mr-1.5" /> Run for a seat</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Register your candidacy</DialogTitle></DialogHeader>
                      <p className="no-gold text-sm text-muted-foreground -mt-1">AI Saint drafts a warm 120-word biography from your answers.</p>
                      <form onSubmit={registerCandidate} className="space-y-4" data-testid="cand-form">
                        <div>
                          <Label>Seat</Label>
                          <Select value={candForm.seat_id} onValueChange={(v) => setCandForm({ ...candForm, seat_id: v })}>
                            <SelectTrigger className="mt-1.5 h-11" data-testid="cand-seat"><SelectValue placeholder="Choose seat" /></SelectTrigger>
                            <SelectContent>
                              {seats.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Years in the community</Label>
                          <Input type="number" min="0" value={candForm.years_in_community} onChange={(e) => setCandForm({ ...candForm, years_in_community: e.target.value })} className="mt-1.5 h-11" data-testid="cand-years" />
                        </div>
                        <div>
                          <Label>Background (a few sentences)</Label>
                          <Textarea value={candForm.background} onChange={(e) => setCandForm({ ...candForm, background: e.target.value })} className="mt-1.5" data-testid="cand-background" />
                        </div>
                        <div>
                          <Label>Priorities</Label>
                          <Textarea value={candForm.priorities} onChange={(e) => setCandForm({ ...candForm, priorities: e.target.value })} className="mt-1.5" placeholder="What you'd focus on if elected" data-testid="cand-priorities" />
                        </div>
                        <DialogFooter>
                          <Button type="submit" disabled={!candForm.seat_id} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="cand-submit"><Wand2 className="w-4 h-4 mr-2" /> Register with AI bio</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {seats.length === 0 && <div className="col-span-full hoa-card p-8 text-center text-muted-foreground" data-testid="seats-empty">No seats yet.</div>}
                {seats.map((s) => (
                  <div key={s.id} className="hoa-card p-5" data-testid={`seat-${s.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="no-gold font-semibold text-base">{s.title}</div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${SEAT_STATUS[s.status]}`}>{s.status.replace("_", " ")}</span>
                    </div>
                    <div className="text-sm text-foreground/85">{s.holder_name || "(vacant)"}</div>
                    <div className="text-xs text-muted-foreground mt-1">Term ends {new Date(s.term_end).toLocaleDateString()} · {s.days_to_expiry != null ? (s.days_to_expiry < 0 ? `${Math.abs(s.days_to_expiry)}d ago` : `in ${s.days_to_expiry}d`) : ""}</div>
                    {isBoard && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                        <Button size="sm" variant="outline" onClick={() => genEmail(s.id)} data-testid={`gen-email-${s.id}`}><Mail className="w-3.5 h-3.5 mr-1" /> Draft email</Button>
                        <Button size="sm" variant="ghost" onClick={() => delSeat(s.id)} data-testid={`del-seat-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {candidates.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold mb-3">Candidates</h4>
                  <div className="space-y-3">
                    {candidates.map((c) => (
                      <div key={c.id} className="hoa-card p-5" data-testid={`candidate-${c.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-gold-soft">Running for {c.seat_title}</div>
                            <div className="no-gold font-semibold text-base">{c.candidate_name}</div>
                          </div>
                          <div className="text-xs text-muted-foreground">{c.years_in_community} yrs here</div>
                        </div>
                        <p className="no-gold text-sm text-foreground/85 mt-2 leading-relaxed whitespace-pre-line" data-testid={`bio-${c.id}`}>{c.biography}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Email draft dialog */}
              <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Open-seat email draft</DialogTitle></DialogHeader>
                  {emailLoading && <div className="text-muted-foreground">AI Saint is drafting…</div>}
                  {emailDraft && (
                    <div className="space-y-3">
                      <div className="text-xs text-muted-foreground">Seat: {emailDraft.seat.title} · term ends {new Date(emailDraft.seat.term_end).toLocaleDateString()}</div>
                      <pre className="no-gold whitespace-pre-wrap font-serif text-[14px] leading-relaxed text-foreground/90 border border-border rounded-lg p-4 bg-accent/20 max-h-[400px] overflow-y-auto" data-testid="email-draft-text">{emailDraft.email}</pre>
                      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(emailDraft.email); toast.success("Copied"); }} data-testid="copy-email-btn">Copy</Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </>
          )}
        </TabsContent>

        {/* Training */}
        <TabsContent value="training" className="mt-6 space-y-6">
          {trainingLocked ? <LockedGate addon="Training Kits" /> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {modules.map((m) => (
                <div key={m.id} className="hoa-card p-6 flex flex-col" data-testid={`module-${m.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/25 border border-gold flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-gold" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-gold-soft">{m.category} · {m.duration_min} min</div>
                  </div>
                  <h3 className="no-gold font-semibold text-lg mb-1">{m.title}</h3>
                  <p className="no-gold text-sm text-foreground/75 mb-4 leading-relaxed flex-1">{m.summary}</p>
                  {m.completed ? (
                    <div className="text-center rounded-full py-2 text-xs font-semibold bg-gold text-secondary-foreground" data-testid={`module-done-${m.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Completed
                    </div>
                  ) : (
                    <Button size="sm" className="rounded-full" onClick={() => completeModule(m.id)} data-testid={`module-complete-${m.id}`}>Mark complete</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Feedback */}
        <TabsContent value="feedback" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">Your voice reaches our developers.</h3>
              <p className="no-gold text-sm text-muted-foreground mt-1">Every idea is kept for our team to review — we decide which revisions to ship.</p>
            </div>
            <Dialog open={fbOpen} onOpenChange={setFbOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110" data-testid="new-feedback-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Submit idea
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Submit feedback</DialogTitle></DialogHeader>
                <form onSubmit={submitFeedback} className="space-y-4" data-testid="feedback-form">
                  <div>
                    <Label>Category</Label>
                    <Select value={fbForm.category} onValueChange={(v) => setFbForm({ ...fbForm, category: v })}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="fb-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="idea">Idea</SelectItem>
                        <SelectItem value="improvement">Improvement</SelectItem>
                        <SelectItem value="bug">Bug</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input required value={fbForm.title} onChange={(e) => setFbForm({ ...fbForm, title: e.target.value })} className="mt-1.5 h-11" data-testid="fb-title" />
                  </div>
                  <div>
                    <Label>Details</Label>
                    <Textarea required value={fbForm.body} onChange={(e) => setFbForm({ ...fbForm, body: e.target.value })} className="mt-1.5 min-h-[120px]" data-testid="fb-body" />
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="fb-submit">Submit</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-3">
            {feedback.length === 0 && (
              <div className="hoa-card p-10 text-center text-muted-foreground" data-testid="feedback-empty">No feedback yet — be the first!</div>
            )}
            {feedback.map((f) => (
              <div key={f.id} className="hoa-card p-5" data-testid={`fb-${f.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${STATUS_COLORS[f.status]}`}>{f.status}</span>
                      <span className="text-[10px] uppercase tracking-widest text-gold-soft">{f.category}</span>
                    </div>
                    <div className="no-gold font-semibold">{f.title}</div>
                    <p className="no-gold text-sm text-foreground/80 mt-1 whitespace-pre-line">{f.body}</p>
                    <div className="text-xs text-muted-foreground mt-2">by {f.author_name} · {new Date(f.created_at).toLocaleDateString()}</div>
                    {f.developer_note && (
                      <div className="mt-3 p-3 rounded-lg bg-gold/10 border border-gold text-xs text-gold" data-testid={`fb-dev-note-${f.id}`}>
                        <b className="uppercase tracking-widest">Developer note</b>: {f.developer_note}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <button onClick={() => upvoteFb(f.id)} className={`w-10 h-10 rounded-lg border flex items-center justify-center ${f.upvoted ? "bg-gold text-secondary-foreground border-gold" : "border-border hover:border-gold"}`} data-testid={`fb-upvote-${f.id}`}>
                      <ThumbsUp className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-mono text-gold">{f.upvotes}</span>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1.5 mt-3 pt-3 border-t border-border">
                    {["reviewing", "planned", "shipped", "rejected"].map((st) => (
                      <Button key={st} size="sm" variant="ghost" onClick={() => setFbStatus(f.id, st)} data-testid={`fb-status-${st}-${f.id}`} className="text-xs capitalize">{st}</Button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
